import type {
  AppConfig,
  Break,
  Dashboard,
  GradedItem,
  PurchaseOrder,
  Sale,
  SealedItem,
  SinglesItem,
} from "./types";

const CONFIG_KEY = "ci_v2_config";
const DEFAULT_API_URL = "https://inventory-api.futuregadgetlabs.com";

export function loadConfig(): AppConfig {
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    if (raw) return JSON.parse(raw) as AppConfig;
  } catch {}
  return { apiUrl: DEFAULT_API_URL, apiToken: "" };
}

export function saveConfig(cfg: AppConfig): void {
  localStorage.setItem(CONFIG_KEY, JSON.stringify(cfg));
}

export function isConfigured(cfg: AppConfig): boolean {
  return cfg.apiUrl !== "" && cfg.apiToken !== "";
}

async function apiFetch<T>(cfg: AppConfig, path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${cfg.apiUrl}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${cfg.apiToken}`,
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as Record<string, string>;
    throw new Error(body["message"] ?? body["error"] ?? `${res.status} ${res.statusText}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ── Purchase Orders ───────────────────────────────────────────────────────────

export async function fetchPurchaseOrders(
  cfg: AppConfig,
  params: { status?: string } = {},
): Promise<PurchaseOrder[]> {
  const q = new URLSearchParams();
  if (params.status) q.set("status", params.status);
  const qs = q.toString() ? `?${q}` : "";
  return apiFetch(cfg, `/api/v1/purchase-orders${qs}`);
}

export async function fetchPurchaseOrder(cfg: AppConfig, id: string): Promise<PurchaseOrder> {
  return apiFetch(cfg, `/api/v1/purchase-orders/${id}`);
}

export async function createPurchaseOrder(
  cfg: AppConfig,
  body: {
    source: string;
    source_url?: string | null;
    seller?: string | null;
    purchased_at: string;
    item_total: string;
    shipping_paid?: string;
    platform_fees?: string;
    notes?: string | null;
    status?: string;
  },
): Promise<PurchaseOrder> {
  return apiFetch(cfg, "/api/v1/purchase-orders", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function patchPurchaseOrder(
  cfg: AppConfig,
  id: string,
  patch: Partial<{
    source: string;
    source_url: string | null;
    seller: string | null;
    purchased_at: string;
    item_total: string;
    shipping_paid: string;
    platform_fees: string;
    notes: string | null;
    status: string;
  }>,
): Promise<PurchaseOrder> {
  return apiFetch(cfg, `/api/v1/purchase-orders/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export async function deletePurchaseOrder(cfg: AppConfig, id: string): Promise<void> {
  return apiFetch(cfg, `/api/v1/purchase-orders/${id}`, { method: "DELETE" });
}

export interface POItem {
  mode: string;
  id: string;
  name: string;
  game: string;
  qty: number;
  cost_basis: string;
  status: string;
  acquired_at: string;
}

export async function fetchPOItems(cfg: AppConfig, poId: string): Promise<POItem[]> {
  return apiFetch(cfg, `/api/v1/purchase-orders/${poId}/items`);
}

// ── Sealed ────────────────────────────────────────────────────────────────────

export async function fetchSealed(
  cfg: AppConfig,
  params: { status?: string; game?: string } = {},
): Promise<SealedItem[]> {
  const q = new URLSearchParams();
  if (params.status) q.set("status", params.status);
  if (params.game) q.set("game", params.game);
  const qs = q.toString() ? `?${q}` : "";
  return apiFetch(cfg, `/api/v1/sealed${qs}`);
}

export async function fetchSealedItem(cfg: AppConfig, id: string): Promise<SealedItem> {
  return apiFetch(cfg, `/api/v1/sealed/${id}`);
}

export async function createSealedItem(
  cfg: AppConfig,
  body: {
    tcgplayer_product_id?: number | null;
    product_name: string;
    set_code?: string | null;
    game: string;
    qty: number;
    cost_per_unit: string;
    po_id?: string | null;
    acquired_at: string;
    status?: string;
    notes?: string | null;
  },
): Promise<SealedItem> {
  return apiFetch(cfg, "/api/v1/sealed", { method: "POST", body: JSON.stringify(body) });
}

export async function patchSealedItem(
  cfg: AppConfig,
  id: string,
  patch: Partial<{
    product_name: string;
    set_code: string | null;
    game: string;
    qty: number;
    cost_per_unit: string;
    po_id: string | null;
    acquired_at: string;
    status: string;
    notes: string | null;
  }>,
): Promise<SealedItem> {
  return apiFetch(cfg, `/api/v1/sealed/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
}

export async function deleteSealedItem(cfg: AppConfig, id: string): Promise<void> {
  return apiFetch(cfg, `/api/v1/sealed/${id}`, { method: "DELETE" });
}

// ── Singles ───────────────────────────────────────────────────────────────────

export async function fetchSingles(
  cfg: AppConfig,
  params: { status?: string; game?: string; finish?: string; condition?: string; q?: string; break_id?: string } = {},
): Promise<SinglesItem[]> {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.game) qs.set("game", params.game);
  if (params.finish) qs.set("finish", params.finish);
  if (params.condition) qs.set("condition", params.condition);
  if (params.q) qs.set("q", params.q);
  if (params.break_id) qs.set("break_id", params.break_id);
  const s = qs.toString() ? `?${qs}` : "";
  return apiFetch(cfg, `/api/v1/singles${s}`);
}

export async function fetchSinglesItem(cfg: AppConfig, id: string): Promise<SinglesItem> {
  return apiFetch(cfg, `/api/v1/singles/${id}`);
}

export async function createSinglesItem(
  cfg: AppConfig,
  body: {
    tcgplayer_product_id?: number | null;
    card_name: string;
    set_code?: string | null;
    set_name?: string | null;
    collector_number?: string | null;
    finish: string;
    condition: string;
    game: string;
    qty?: number;
    cost_basis: string;
    po_id?: string | null;
    break_id?: string | null;
    acquired_at: string;
    status?: string;
    notes?: string | null;
  },
): Promise<SinglesItem> {
  return apiFetch(cfg, "/api/v1/singles", { method: "POST", body: JSON.stringify(body) });
}

export async function patchSinglesItem(
  cfg: AppConfig,
  id: string,
  patch: Partial<{
    card_name: string;
    set_code: string | null;
    set_name: string | null;
    finish: string;
    condition: string;
    game: string;
    qty: number;
    cost_basis: string;
    status: string;
    notes: string | null;
  }>,
): Promise<SinglesItem> {
  return apiFetch(cfg, `/api/v1/singles/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
}

export async function deleteSinglesItem(cfg: AppConfig, id: string): Promise<void> {
  return apiFetch(cfg, `/api/v1/singles/${id}`, { method: "DELETE" });
}

// ── Graded ────────────────────────────────────────────────────────────────────

export async function fetchGraded(
  cfg: AppConfig,
  params: { status?: string; game?: string; grading_co?: string } = {},
): Promise<GradedItem[]> {
  const q = new URLSearchParams();
  if (params.status) q.set("status", params.status);
  if (params.game) q.set("game", params.game);
  if (params.grading_co) q.set("grading_co", params.grading_co);
  const qs = q.toString() ? `?${q}` : "";
  return apiFetch(cfg, `/api/v1/graded${qs}`);
}

export async function fetchGradedItem(cfg: AppConfig, id: string): Promise<GradedItem> {
  return apiFetch(cfg, `/api/v1/graded/${id}`);
}

export async function createGradedItem(
  cfg: AppConfig,
  body: {
    tcgplayer_product_id?: number | null;
    raw_tcgplayer_product_id?: number | null;
    card_name: string;
    set_code?: string | null;
    set_name?: string | null;
    collector_number?: string | null;
    game: string;
    grading_co: string;
    cert_number?: string | null;
    grade?: string | null;
    cost_basis: string;
    po_id?: string | null;
    acquired_at: string;
    graded_at?: string | null;
    status?: string;
    notes?: string | null;
  },
): Promise<GradedItem> {
  return apiFetch(cfg, "/api/v1/graded", { method: "POST", body: JSON.stringify(body) });
}

export async function patchGradedItem(
  cfg: AppConfig,
  id: string,
  patch: Partial<{
    card_name: string;
    game: string;
    grading_co: string;
    cert_number: string | null;
    grade: string | null;
    cost_basis: string;
    graded_at: string | null;
    status: string;
    notes: string | null;
  }>,
): Promise<GradedItem> {
  return apiFetch(cfg, `/api/v1/graded/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
}

export async function deleteGradedItem(cfg: AppConfig, id: string): Promise<void> {
  return apiFetch(cfg, `/api/v1/graded/${id}`, { method: "DELETE" });
}

// ── Sales ─────────────────────────────────────────────────────────────────────

export async function fetchSales(
  cfg: AppConfig,
  params: { mode?: string; platform?: string } = {},
): Promise<Sale[]> {
  const q = new URLSearchParams();
  if (params.mode) q.set("mode", params.mode);
  if (params.platform) q.set("platform", params.platform);
  const qs = q.toString() ? `?${q}` : "";
  return apiFetch(cfg, `/api/v1/sales${qs}`);
}

export async function createSale(
  cfg: AppConfig,
  body: {
    mode: string;
    item_id: string;
    platform: string;
    sold_at: string;
    sale_price: string;
    platform_fees?: string;
    shipping_out?: string;
    buyer?: string | null;
    notes?: string | null;
  },
): Promise<Sale> {
  return apiFetch(cfg, "/api/v1/sales", { method: "POST", body: JSON.stringify(body) });
}

// ── Breaks ────────────────────────────────────────────────────────────────────

export async function fetchBreaks(cfg: AppConfig, sealedId: string): Promise<Break[]> {
  return apiFetch(cfg, `/api/v1/sealed/${sealedId}/breaks`);
}

export async function createBreak(
  cfg: AppConfig,
  body: {
    sealed_item_id: string;
    qty_broken: number;
    broken_at: string;
    notes?: string | null;
  },
): Promise<Break> {
  return apiFetch(cfg, "/api/v1/breaks", { method: "POST", body: JSON.stringify(body) });
}

export interface IngestCSVResult {
  break_id: string;
  sealed_item_id: string;
  singles_created: number;
  cost_per_single: string;
}

export async function ingestBreakCSV(cfg: AppConfig, breakId: string, file: File): Promise<IngestCSVResult> {
  const res = await fetch(`${cfg.apiUrl}/api/v1/breaks/${breakId}/ingest-csv`, {
    method: "POST",
    headers: { Authorization: `Bearer ${cfg.apiToken}` },
    body: file,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as Record<string, string>;
    throw new Error(body["message"] ?? body["error"] ?? `${res.status} ${res.statusText}`);
  }
  return res.json() as Promise<IngestCSVResult>;
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

export async function fetchDashboard(cfg: AppConfig): Promise<Dashboard> {
  return apiFetch(cfg, "/api/v1/dashboard");
}
